import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils/common'
import { MAX_TEXT_LENGTH, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

import { useReportingEventList } from '../../../reporting-events/hooks'
import { ReportingEvent } from '../../../reporting-events/types'
import { MAX_REPORTING_EVENTS } from '../../constants'

import { SelectedEventPills } from './SelectedEventPills'
import { useEventSelection } from './useEventSelection'

const SORT_BY_NAME_ASC = '+reporting_event_name'

interface ReportingEventsInputProps {
  value: string[]
  onChange: (value: string[], events: ReportingEvent[]) => void
  selectedEvents?: ReportingEvent[]
  disabled?: boolean
  onError?: (hasError: boolean, message?: string) => void
}

export const ReportingEventsInput: React.FC<ReportingEventsInputProps> = ({
  value,
  onChange,
  disabled = false,
  selectedEvents: initialSelectedEvents = [],
  onError,
}) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const trimmedSearch = debouncedSearchText.trim()
  const hasSearchText = trimmedSearch.length > 0

  const { data: initialData, isLoading: isLoadingInitial } =
    useReportingEventList({ sort_by: SORT_BY_NAME_ASC }, { enabled: !disabled })

  const { data: searchResults, isLoading: isLoadingSearch } =
    useReportingEventList(
      { q: trimmedSearch, sort_by: SORT_BY_NAME_ASC },
      { enabled: !disabled && hasSearchText }
    )

  const currentData = useMemo(
    () => (hasSearchText ? searchResults : initialData),
    [hasSearchText, searchResults, initialData]
  )
  const isLoading = useMemo(
    () => (hasSearchText ? isLoadingSearch : isLoadingInitial),
    [hasSearchText, isLoadingSearch, isLoadingInitial]
  )

  const {
    selectedEvents,
    handleSelectEvent: handleSelectEventAction,
    handleRemoveEvent,
    handleCreateAndSelect,
    createMutation,
  } = useEventSelection({
    value,
    onChange,
    initialSelectedEvents,
    onError,
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const availableResults = useMemo(
    () => currentData?.data?.filter((event) => !value.includes(event.id)) ?? [],
    [currentData?.data, value]
  )

  const isDuplicateOfSelected = useMemo(
    () =>
      selectedEvents.some(
        (event) =>
          event.reporting_event_name.toLowerCase() ===
          trimmedSearch.toLowerCase()
      ),
    [selectedEvents, trimmedSearch]
  )

  const shouldShowCreateOption =
    trimmedSearch.length > 0 &&
    !isLoading &&
    availableResults.length === 0 &&
    !isDuplicateOfSelected

  const handleSelectEvent = useCallback(
    (event: ReportingEvent) => {
      handleSelectEventAction(event)
      setSearchText('')
      setIsOpen(false)
    },
    [handleSelectEventAction]
  )

  const handleCreateAndSelectWrapper = useCallback(async () => {
    if (createMutation.isPending) return
    await handleCreateAndSelect(trimmedSearch)
    setSearchText('')
    setIsOpen(false)
  }, [handleCreateAndSelect, trimmedSearch, createMutation.isPending])

  const handleClearSearch = useCallback(() => {
    setSearchText('')
  }, [])

  const handleBlur = useCallback(() => {
    setSearchText('')
  }, [])

  const renderCommandContent = () => {
    if (isLoading) {
      return (
        <div className="py-3 pl-2 pr-8 text-sm text-gray-500">
          {t('common.searching')}...
        </div>
      )
    }

    if (shouldShowCreateOption) {
      return (
        <CommandGroup>
          <CommandItem
            value={`create-${trimmedSearch}`}
            onSelect={handleCreateAndSelectWrapper}
            disabled={createMutation.isPending}
            className="rounded-md cursor-pointer py-2.5 px-3"
          >
            <span className="break-words">
              {createMutation.isPending
                ? `${t('common.creating')}...`
                : `${trimmedSearch} (${t('project.dialog.createNewReportingEvent')})`}
            </span>
          </CommandItem>
        </CommandGroup>
      )
    }

    if (availableResults.length > 0) {
      return (
        <CommandGroup className="p-0">
          {availableResults.map((event) => (
            <CommandItem
              key={event.id}
              value={event.reporting_event_name}
              onSelect={() => handleSelectEvent(event)}
              className="rounded-md cursor-pointer py-2.5 px-3"
            >
              <span className="break-words whitespace-normal">
                {event.reporting_event_name}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      )
    }

    return (
      <CommandEmpty>
        <div className="py-1.5 px-2 text-sm text-gray-500">
          {t('common.noResults')}
        </div>
      </CommandEmpty>
    )
  }

  return (
    <div className="space-y-3">
      <div ref={containerRef} className="relative">
        <Command
          className="relative overflow-visible bg-transparent"
          shouldFilter={false}
        >
          <CommandInput
            placeholder={
              value.length >= MAX_REPORTING_EVENTS
                ? t('project.dialog.validation.maxReportingEventsReached', {
                    max: MAX_REPORTING_EVENTS,
                  })
                : t('project.dialog.placeholders.enterReportingEvent')
            }
            value={searchText}
            onValueChange={setSearchText}
            maxLength={MAX_TEXT_LENGTH}
            onFocus={() => setIsOpen(true)}
            disabled={disabled || value.length >= MAX_REPORTING_EVENTS}
            clearable={!!searchText}
            onClear={handleClearSearch}
            onBlur={handleBlur}
          />

          {isOpen && (
            <CommandList
              className={cn(
                'select-scrollbar',
                'absolute top-full left-0 w-full mt-2 max-h-50 overflow-y-auto',
                'rounded-md border border-input bg-white shadow-lg',
                'z-[9999]'
              )}
            >
              {renderCommandContent()}
            </CommandList>
          )}
        </Command>
      </div>
      {value.length >= MAX_REPORTING_EVENTS && (
        <p className="mt-1 text-xs text-error-600">
          {t('project.dialog.validation.maxReportingEvents', {
            max: MAX_REPORTING_EVENTS,
          })}
        </p>
      )}
      <SelectedEventPills
        selectedEvents={selectedEvents}
        disabled={disabled}
        onRemove={handleRemoveEvent}
      />
    </div>
  )
}
